import type { TFunction } from 'i18next';

// `toLocaleDateString` silently ignores time-component options (`hour`,
// `minute`) in some engines, so we format the date and time separately and
// concatenate, mirroring the approach in `common/utils/dates.ts`.
export const formatDate = (iso: string): string => {
  const date = new Date(iso);
  const datePart = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
  return `${datePart} ${timePart}`;
};

// Binary units, labeled with the conventional short forms.
export const formatFileSize = (bytes: number): string => {
  const units = ['KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 'B';
  for (const next of units) {
    if (value < 1024) {
      break;
    }
    value /= 1024;
    unit = next;
  }
  return unit === 'B' ? `${value} B` : `${value.toFixed(1)} ${unit}`;
};

export const snakeToTitleCase = (s: string): string =>
  s
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const getCategoryLabel = (t: TFunction, category: string): string => {
  const labels: Record<string, string> = {
    authentication: t('Authentication'),
    access_control: t('Access Control'),
    system_configuration: t('System Configuration')
  };
  return labels[category] || snakeToTitleCase(category);
};

export const getExportStatusLabel = (t: TFunction, status: string): string => {
  const labels: Record<string, string> = {
    pending: t('Pending'),
    in_progress: t('In Progress'),
    completed: t('Completed'),
    failed: t('Failed')
  };
  return labels[status] || snakeToTitleCase(status);
};

export const getEventTypeLabel = (t: TFunction, eventType: string): string => {
  const labels: Record<string, string> = {
    login: t('Login'),
    logout: t('Logout'),
    login_failed: t('Login Failed'),
    member_removed: t('Member Removed'),
    product_access_changed: t('Product Access Changed'),
    bulk_product_access_removed: t('Bulk Product Access Removed'),
    bulk_product_access_updated: t('Bulk Product Access Updated'),
    invitation_sent: t('Invitation Sent'),
    invitation_revoked: t('Invitation Revoked'),
    invitation_accepted: t('Invitation Accepted'),
    invitation_resent: t('Invitation Resent'),
    enterprise_invitation_sent: t('Enterprise Invitation Sent'),
    admin_removed: t('Admin Removed'),
    subscription_changed: t('Subscription Changed'),
    team_created: t('Team Created'),
    team_deleted: t('Team Deleted'),
    team_membership_changed: t('Team Membership Changed'),
    user_role_changed: t('User Role Changed'),
    user_added: t('User Added'),
    user_edited: t('User Edited'),
    user_removed: t('User Removed'),
    settings_changed: t('Settings Changed'),
    user_settings_changed: t('User Settings Changed'),
    api_key_created: t('API Key Created'),
    api_key_updated: t('API Key Updated'),
    api_key_deleted: t('API Key Deleted'),
    security_log_exported: t('Security Log Exported')
  };
  return labels[eventType] || snakeToTitleCase(eventType);
};
